/* eslint-disable indent */
/* eslint-disable comma-dangle */
/* eslint-disable import/extensions */
import {
  CopyOutlined,
  DeleteOutlined,
  FileExcelOutlined,
  SaveOutlined,
  SearchOutlined,
  SyncOutlined,
  TagOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Avatar,
  Button,
  Card,
  Dropdown,
  Input,
  List,
  Menu,
  Modal,
  notification,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { ColumnsType } from "antd/es/table";
import copy from "copy-to-clipboard";
import { ExportToCsv } from "export-to-csv";
import React, { useEffect, useRef, useState } from "react";
import Highlighter from "react-highlight-words";

import { DiscordRoles } from "../../../types/discord_roles";
import { Tags } from "../../../types/tags";
import { getGuildTags } from "../../lib/discordQueries";
import {
  AllMembers,
  AtRiskMember,
  DiscordMemberRpc,
  MemberTag,
  ParsedCSVMember,
} from "../../lib/discordTypes";
import DateRangePicker from "../DateRangePicker/DateRangePicker";
import MemberTags from "../MemberTags/MemberTags";
import BulkEdit from "./BulkEdit";
import { useDiscordRolesContext } from "./DiscordRolesContext";
import MemberDetail from "./MemberDetail";

const { Option } = Select;

type DiscordMemberProps = {
  members: // list of members
  AtRiskMember[] | AllMembers[] | DiscordMemberRpc[] | null | undefined;
  membersFiltered?: // list of filtered members by role
  AtRiskMember[] | AllMembers[] | DiscordMemberRpc[] | null | undefined;
  isLoading: boolean; // whether to show the loading
  description?: string | null; // description to show on top of table
  otherColumns?: // other columns to show in addtion
  ColumnsType<AllMembers | AtRiskMember | DiscordMemberRpc> | undefined | null;
  atRisk?: boolean; // whether to show at risk columns specific
  showActivityColumns?: boolean; // whether to show at risk columns specific
  startTime: string; // start time for data queries, in "MM/DD/YY" format
  endTime: string; // end time for data queries, in "MM/DD/YY" format
  guildTags?: Tags[]; // existing tags for the Discord guild
  memberUpdate?: any; // callack to update filtered members by role in parent component
  memberReset?: () => void; // callback to reset the filtered members in parent component
  deleteTab?: () => Promise<void>; // callback to call in parent to delete the current custom view
  loadPageData?: (startTime?: string, endTime?: string) => Promise<void>; // callback to call in parent to fetch user/member data for the current custom view
  onBulkTag?: () => Promise<void>; // callback to call in parent to fetch user/member data for all tables (used when bulk tagging)
  onGuildTagUpdate?: () => Promise<void>; // callback to call in parent if a new guild tag is added
  onMemberTagAdded?: (memberId: string, tag: MemberTag) => Promise<void>; // callback to call in parent if a new member tag is added
  onMemberTagRemoved?: (memberId: string, tag: MemberTag) => Promise<void>; // callback to call in parent if a member tag is removed
  showDatePicker?: boolean; // whether to show or not the date picker in the table header
  setIsBulkEditModalVisible?: React.Dispatch<React.SetStateAction<boolean>>; // callback in parent to set the visibility of Bulk Tag modal
  isBulkEditModalVisible?: boolean; // Wether the Bulk Tag modal is visible or not
  isCustomView?: boolean; // wether the current table is part of a custom view or not
};

function DiscordMembersTable({
  members = null,
  membersFiltered = null,
  isLoading = false,
  description = null,
  otherColumns = null,
  atRisk = false,
  showActivityColumns = false,
  startTime = "",
  endTime = "",
  guildTags = [],
  memberUpdate,
  memberReset,
  deleteTab,
  loadPageData,
  onGuildTagUpdate,
  onMemberTagAdded,
  onMemberTagRemoved,
  showDatePicker = true,
  setIsBulkEditModalVisible,
  isBulkEditModalVisible = false,
  isCustomView = false,
  onBulkTag,
}: DiscordMemberProps): JSX.Element {
  const guildId: string = localStorage.getItem("guild-id") || "";
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [isUserModalVisible, setIsUserModalVisible] = useState(false);
  const [selectedMember, setSelectedMember] = useState<
    AtRiskMember | AllMembers | DiscordMemberRpc | null
  >(null);
  const [isMemberSelected, setIsMemberSelected] = useState<boolean>(false);
  const [selectedMembers, setSelectedMembers] = useState<
    AtRiskMember[] | AllMembers[] | DiscordMemberRpc[] | null
  >([]);
  const [allGuildTags, setAllGuildTags] = useState<Tags[]>([]);
  const [loadingGuildTags, setLoadingGuildTags] = useState<boolean>(false);
  const [otherRoles, setOtherRoles] = useState<DiscordRoles[]>([]);
  const [isRolesModalVisible, setRolesModalVisible] = useState<boolean>(false);

  type DiscordRolesObject = {
    roles: DiscordRoles[];
  };

  const [filterSearch, setFilterSearch] = useState<DiscordRolesObject>({
    roles: [],
  });

  const selectEl = useRef(null);

  const guildRoles = useDiscordRolesContext();

  /**
   * Load and set all tags for the given Discord guild
   */
  const loadGuildTags = async (): Promise<void> => {
    setLoadingGuildTags(true);
    try {
      const tags = await getGuildTags(guildId);
      if (tags) {
        setAllGuildTags(tags);
      }
    } finally {
      setLoadingGuildTags(false);
      if (setIsBulkEditModalVisible) {
        setIsBulkEditModalVisible(false);
      }
    }
  };

  useEffect(() => {
    if (guildTags.length === 0) {
      loadGuildTags();
    }
  }, []);

  /**
   * Updates the selected role tags
   *
   */
  const handleTagChange = (value: any) => {
    const prevFilterInputs = { ...filterSearch };
    const prevFilterData = {
      ...prevFilterInputs,
      roles: value,
    };
    setFilterSearch(prevFilterData);
  };

  /**
   * Filters the Discord members to show only those who have the selected roles
   *
   */
  const handleFilterSearch = (e: any) => {
    e.preventDefault();
    if (memberReset) {
      memberReset();
    }
    const filteredRecords = members?.filter((record: any) => {
      const hasRoles = record?.discord_roles?.filter((role: any) => {
        return filterSearch.roles.includes(role.name);
      });
      if (hasRoles?.length && filterSearch?.roles.length) {
        return hasRoles;
      }
      return false;
    });
    if (filteredRecords && memberUpdate) {
      memberUpdate(filteredRecords);
    }
  };

  /**
   * Resets the table Discord Members table to show all users
   */
  const handleFilterReset = () => {
    setFilterSearch({ roles: [] });
    if (memberReset) {
      memberReset();
    }
  };

  const handleSearch = ({ selectedKeys, confirm, dataIndex }: any) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: any) => {
    clearFilters();
    setSearchText("");
  };

  /**
   * When a new guild tag was added, call the onGuildTagUpdate to alert the parent
   */
  const teamTagsChange = () => {
    if (onGuildTagUpdate) onGuildTagUpdate();
  };

  /**
   * When a new member tag was added, call the onMemberTagAdded prop to alert the parent
   * @param memberId Discord member id
   * @param tag tag that was added to the member
   */
  const memberTagsAdded = (memberId: string, tag: MemberTag) => {
    if (onMemberTagAdded) onMemberTagAdded(memberId, tag);
  };

  /**
   * When a  member tag was removed, call the onMemberTagRemoved prop to alert the parent
   * @param memberId Discord member id
   * @param tag tag that was removed from the member
   */
  const memberTagsRemoved = (memberId: string, tag: MemberTag) => {
    if (onMemberTagRemoved) onMemberTagRemoved(memberId, tag);
  };

  /**
   * Display the member detail modal for the given member.
   * @param member the given member that was clicked on
   */
  const showUserModal = async (
    member: AtRiskMember | AllMembers | DiscordMemberRpc
  ) => {
    setIsUserModalVisible(true);
    setSelectedMember(member);
  };

  /**
   * Spreads the necessary props into the username column in order to show the search widget
   * @param dataIndex the dataIndex we want to search in the column
   * @returns An object with the necesarry props to show the column's search widget
   */
  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: any) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={() => {
            // eslint-disable-next-line no-unused-vars
          }}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch({ selectedKeys, confirm, dataIndex })
          }
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch({ selectedKeys, confirm, dataIndex })}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: any) => (
      <SearchOutlined
        style={{ fontSize: 20, color: filtered ? "#1890ff" : undefined }}
      />
    ),
    onFilter: (value: any, record: any) => {
      return record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "";
    },
    // onFilterDropdownVisibleChange: () => {},
    render: (text: string) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  /**
   *  Spreads the necessary props into the Roles column in order to show the search roles widget
   * @param guildRoles - An array with all the guild roles available
   * @returns An object with the necessary props to show the search roles widget
   */
  // eslint-disable-next-line
  const getColumnSearchPropsTags = (guildRoles: DiscordRoles[] | null) => ({
    filterDropdown: () => (
      <div style={{ padding: 8, display: "flex", flexDirection: "column" }}>
        <Select
          mode="multiple"
          showArrow
          style={{ width: "12rem", borderRadius: 10, marginBottom: 8 }}
          ref={selectEl}
          onChange={handleTagChange}
          placeholder="Select one or more roles"
        >
          {guildRoles &&
            guildRoles.map((item) => {
              return (
                <Option
                  key={item.discord_id}
                  value={item.name}
                  name={item.name}
                >
                  <Tag>{item.name}</Tag>
                </Option>
              );
            })}
        </Select>
        <Space>
          <Button
            type="primary"
            onClick={handleFilterSearch}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={handleFilterReset}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: any) => (
      <SearchOutlined
        style={{ fontSize: 20, color: filtered ? "#1890ff" : undefined }}
      />
    ),
  });

  /**
   * Pops a notification after using the Export menu
   * @param users Discord members
   * @param type string, "members" for exporting members, "id" for copying member IDs
   */
  const openNotification = (users: any, type: string) => {
    const messages: { [key: string]: any } = {
      id: "IDs copied!",
      members: "members info exported!",
    };
    // eslint-disable-next-line
    notification["success"]({
      message: "Success!",
      description: `${users.length} ${messages[type]}!`,
    });
  };

  /**
   * Parses a Discord member and removes the id and discord_id properties which are not
   * relevant in the exported CSV file
   *
   * @param member : The member to parse fro CSV export
   * @returns The parsed member without the id and discord_id properties
   */
  function parseMember(member: AllMembers): ParsedCSVMember {
    const parsedMember: ParsedCSVMember = member;
    delete parsedMember.key;
    return parsedMember;
  }

  /**
   * This function takes the table's Discord members and exports them as a CSV file
   */
  const exportMembers = () => {
    const csvOptions = {
      fieldSeparator: ",",
      quoteStrings: '"', // eslint-disable-line
      decimalSeparator: ".",
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true,
      fileName: "Dots exported members",
      // headers: ['Column 1', 'Column 2', etc...] <-- Won't work with useKeysAsHeaders present!
    };
    const users = membersFiltered || members || undefined;
    const exportedMembers = isMemberSelected ? selectedMembers : users;
    const membersWithTags =
      exportedMembers &&
      exportedMembers.map((member) => {
        let tags: any = [];
        let discord_roles: any = [];
        const parsedMember = parseMember(member);
        if (parsedMember.tags && !parsedMember.discord_roles) {
          tags = parsedMember.tags.map((e) => e.name).join(", ");
          return {
            ...parsedMember,
            tags,
          };
        }

        if (parsedMember.tags && parsedMember.discord_roles) {
          tags = parsedMember.tags.map((e) => e.name).join(", ");
          discord_roles = parsedMember.discord_roles
            .map((e) => e.name)
            .join(", ");
          return {
            ...parsedMember,
            tags,
            discord_roles,
          };
        }

        if (!parsedMember.tags && parsedMember.discord_roles) {
          discord_roles = parsedMember.discord_roles
            .map((e) => e.name)
            .join(", ");
          return {
            ...parsedMember,
            discord_roles,
          };
        }
        // delete parsedMember.key;
        return parsedMember;
      });
    const csvExporter = new ExportToCsv(csvOptions);
    csvExporter.generateCsv(membersWithTags);
    openNotification(exportedMembers, "members");
  };

  /**
   * This function copies the table's Discord members ids to the user's clipboard
   */
  const copyIds = () => {
    const users = membersFiltered || members || undefined;
    const copyMembers = isMemberSelected ? selectedMembers : users;
    const copyUsers =
      copyMembers &&
      copyMembers.map((user) => {
        return user.discord_id;
      });
    if (copyUsers) copy(copyUsers.join("\r\n"));
    openNotification(copyUsers, "id");
  };

  /**
   * Handles the selection of rows (users in the table)
   */
  function onChange<Type extends AtRiskMember | AllMembers | DiscordMemberRpc>(
    selectedRowKeys: Type[] | null,
    selectedRows: Type[] | null
  ) {
    setSelectedMembers(selectedRows);
    if (selectedRowKeys) {
      if (selectedRowKeys.length > 0) {
        setIsMemberSelected(true);
      } else {
        setIsMemberSelected(false);
      }
    }
  }

  /**
   * Handles the selection of rows (users in the table)
   * @see onChange
   */
  const rowSelection: {
    [key: string]: (
      selectedRowKeys:
        | AtRiskMember[]
        | AllMembers[]
        | DiscordMemberRpc[]
        | null,
      selectedRows: AtRiskMember[] | AllMembers[] | DiscordMemberRpc[] | null
    ) => void;
  } = {
    onChange,
  };

  /**
   * Makes the bulk tag modal visible.
   */
  const bulkTagMembers = () => {
    if (setIsBulkEditModalVisible) {
      setIsBulkEditModalVisible(true);
    }
  };

  /**
   * Reloads the guild tags and the table's member data after saving tags
   * in bulk
   */
  const handleBulkTagSave = () => {
    if (onGuildTagUpdate && loadPageData) {
      onGuildTagUpdate();
      if (isCustomView) {
        loadPageData(startTime, endTime);
      } else {
        loadPageData();
      }
      if (onBulkTag) onBulkTag();
    }
    if (setIsBulkEditModalVisible) {
      setIsBulkEditModalVisible(true);
    }
  };

  const Exportmenu = (
    <Menu>
      <Menu.Item key="1" icon={<FileExcelOutlined />} onClick={exportMembers}>
        {isMemberSelected ? "Export selected to CSV" : "Export all to CSV"}
      </Menu.Item>
      <Menu.Item key="2" icon={<CopyOutlined />} onClick={copyIds}>
        {isMemberSelected
          ? "Copy selected Discord IDs"
          : "Copy all Discord IDs"}
      </Menu.Item>
    </Menu>
  );

  const showUserRoles = (roles: DiscordRoles[]) => {
    setRolesModalVisible(true);
    setOtherRoles(roles);
  };

  const closeUserRoles = () => {
    setRolesModalVisible(false);
    setOtherRoles([]);
  };

  // default columns for all members
  let allUserscolumns: ColumnsType<
    AllMembers | AtRiskMember | DiscordMemberRpc
  > = [
    {
      key: "username",
      title: "Username",
      dataIndex: "username",
      ...getColumnSearchProps("username"),
      render: (text: string, record: AllMembers) => (
        <div style={{ display: "flex", justifyContent: "flex-start" }}>
          <div>
            <Avatar
              src={
                record.avatar
                  ? `https://cdn.discordapp.com/avatars/${record.discord_id}/${record.avatar}.png`
                  : "/avatar_slack.png"
              }
              style={{ marginRight: 15 }}
            />
          </div>
          <div style={{ maxWidth: "200px" }}>
            <Tooltip title="Click to view member activity">
              <div
                style={{ color: "#1890ff", cursor: "pointer" }}
                onClick={() => showUserModal(record)}
                onKeyDown={() => showUserModal(record)}
                tabIndex={0}
                role="button"
              >
                {text}
              </div>
            </Tooltip>
          </div>
        </div>
      ),
      width: "20%",
      sorter: (a: AllMembers, b: AllMembers) =>
        a.username.localeCompare(b.username),
    },
    {
      title: "Tags / Cohorts",
      dataIndex: "tags",
      key: "tags",
      render: (_text, record) => (
        <MemberTags
          member={record}
          teamTags={guildTags.length === 0 ? allGuildTags : guildTags}
          memberTags={record.tags ? [...record.tags] : null}
          memberId={record.id}
          onTeamTagAdded={teamTagsChange}
          onMemberTagAdded={memberTagsAdded}
          onMemberTagRemoved={memberTagsRemoved}
        />
      ),
      width: "20%",
    },
    {
      key: "discord_roles",
      title: "Roles",
      dataIndex: "discord_roles",
      ...getColumnSearchPropsTags(guildRoles.guildRoles),
      render: (discord_roles) => (
        <>
          {discord_roles.slice(0, 3).map((role: DiscordRoles) => {
            return <Tag style={{ marginBottom: 6 }}>{role.name}</Tag>;
          })}
          <div
            style={{ color: "#1890ff", cursor: "pointer" }}
            onClick={() => showUserRoles(discord_roles)}
            onKeyDown={() => showUserRoles(discord_roles)}
            tabIndex={0}
            role="button"
          >
            {discord_roles.length > 6
              ? `+ ${discord_roles.length - 3} more`
              : null}
          </div>
        </>
      ),
      width: "20%",
    },
  ];

  // Columns for at risk members
  const atRiskColumns: ColumnsType<AtRiskMember> = [
    {
      key: "last_month_count",
      title: "Last period",
      dataIndex: "last_month_count",
      render: (text) => <p>{text}</p>,
      width: "15%",
      sorter: (a, b) => {
        return a.last_month_count - b.last_month_count;
      },
    },
    {
      key: "this_month_count",
      title: "Current period",
      dataIndex: "this_month_count",
      render: (text) => <p>{text}</p>,
      width: "15%",
      sorter: (a, b) => {
        return a.this_month_count - b.this_month_count;
      },
    },
  ];

  // Columns for activity count
  const activityColumns: ColumnsType<DiscordMemberRpc> = [
    {
      key: "parent_count",
      title: "Channel msgs",
      dataIndex: "parent_count",
      render: (text) => <p>{text}</p>,
      width: "10%",
      sorter: (a, b) => {
        return a.parent_count - b.parent_count;
      },
    },
    {
      key: "thread_count",
      title: "Thread msgs",
      dataIndex: "thread_count",
      render: (text) => <p>{text}</p>,
      width: "10%",
      sorter: (a, b) => {
        return a.thread_count - b.thread_count;
      },
    },
    {
      key: "reaction_count",
      title: "Reactions",
      dataIndex: "reaction_count",
      render: (text) => <p>{text}</p>,
      width: "10%",
      sorter: (a, b) => {
        return a.reaction_count - b.reaction_count;
      },
    },
    {
      key: "total_count",
      title: "Total activity",
      dataIndex: "total_count",
      render: (text) => <p>{text}</p>,
      width: "10%",
      sorter: (a, b) => {
        return a.total_count - b.total_count;
      },
    },
  ];

  // if more columns are passed show them
  if (otherColumns) {
    allUserscolumns[0].width = "30%";
    allUserscolumns[1].width = "30%";
    allUserscolumns[2].width = "25%";
    allUserscolumns = allUserscolumns.concat(otherColumns);
  }

  // if at risk value, then show at risk. Otherwise show activity column
  if (atRisk) {
    allUserscolumns[0].width = "30%";
    allUserscolumns = allUserscolumns.concat(
      atRiskColumns as ColumnsType<AllMembers | AtRiskMember | DiscordMemberRpc>
    );
  } else if (showActivityColumns) {
    allUserscolumns = allUserscolumns.concat(
      activityColumns as ColumnsType<
        AllMembers | AtRiskMember | DiscordMemberRpc
      >
    );
  }

  return (
    <div>
      <Spin spinning={isLoading}>
        {description ? (
          <Alert
            style={{
              marginBottom: 20,
              marginRight: 40,
              marginLeft: 40,
              background: "#e6f7ff",
              borderColor: "#93d6ff",
            }}
            type="info"
            message={description}
          />
        ) : null}
        <Card
          title={
            <>
              {showDatePicker ? (
                <DateRangePicker
                  loadPageData={loadPageData}
                  customOption={{ disabled: false }}
                  rangeOfSelector={{
                    range: "day",
                    selectorDefaultOptions: [
                      { value: 7, disabled: false },
                      {
                        value: 30,
                        disabled: false,
                      },
                    ],
                  }}
                />
              ) : null}
            </>
          }
          extra={
            <Space>
              <span style={{ fontStyle: "italic", color: "gray" }}>
                (Total members: {membersFiltered && membersFiltered.length} )
              </span>
              {isMemberSelected ? (
                <>
                  <Button
                    icon={<TagOutlined />}
                    size="small"
                    onClick={() => bulkTagMembers()}
                  >
                    Bulk tag members
                  </Button>
                </>
              ) : null}
              <Dropdown
                disabled={members === null || members.length === 0}
                overlay={Exportmenu}
              >
                <Tooltip title="Pick an option below">
                  <Button
                    disabled={members === null || members.length === 0}
                    type="primary"
                    size="small"
                    icon={<SaveOutlined />}
                  >
                    Export
                  </Button>
                </Tooltip>
              </Dropdown>
              {deleteTab ? (
                <Popconfirm
                  placement="topLeft"
                  title="Delete this view?"
                  okText="Yes"
                  cancelText="No"
                  onConfirm={deleteTab}
                >
                  <Button danger size="small">
                    <DeleteOutlined />
                  </Button>
                </Popconfirm>
              ) : null}
              <Button
                danger
                size="small"
                onClick={
                  loadPageData
                    ? () => loadPageData(startTime, endTime)
                    : undefined
                }
              >
                <SyncOutlined />
              </Button>
            </Space>
          }
        >
          <Table<AllMembers | AtRiskMember | DiscordMemberRpc>
            size="small"
            columns={allUserscolumns}
            rowSelection={{ type: "checkbox", ...rowSelection }}
            dataSource={
              membersFiltered
                ? [...membersFiltered]
                : members
                ? [...members]
                : undefined
            }
            rowKey="id"
            pagination={{
              defaultPageSize: 50,
              showSizeChanger: true,
              pageSizeOptions: ["50", "100", "200", "1000"],
            }}
          />
          <Modal
            width={1200}
            title={
              selectedMember
                ? `${selectedMember.username}'s activity`
                : "Member activity"
            }
            style={{ top: 40, right: 10, borderRadius: 20 }}
            visible={isUserModalVisible}
            onCancel={() => setIsUserModalVisible(false)}
            footer={[
              <Button key="submit" onClick={() => setIsUserModalVisible(false)}>
                Ok
              </Button>,
            ]}
          >
            <MemberDetail
              onTeamTagAdded={teamTagsChange}
              discordUser={selectedMember}
              onMemberTagAdded={memberTagsAdded}
              onMemberTagRemoved={memberTagsRemoved}
              platform="discord"
            />
          </Modal>
        </Card>
      </Spin>
      <Modal
        title="Bulk edit"
        visible={isBulkEditModalVisible}
        onCancel={() => {
          if (setIsBulkEditModalVisible) {
            setIsBulkEditModalVisible(false);
          }
        }}
        footer={[]}
      >
        <BulkEdit
          selectedMembers={selectedMembers}
          handleSave={handleBulkTagSave}
        />
      </Modal>
      <Modal
        title="User roles"
        visible={isRolesModalVisible}
        onCancel={closeUserRoles}
        footer={[]}
      >
        <List
          size="small"
          pagination={{ pageSize: 10 }}
          bordered
          dataSource={otherRoles}
          renderItem={(item) => <List.Item>{item.name}</List.Item>}
        />
      </Modal>
    </div>
  );
}

export default DiscordMembersTable;
